/**
 * 脱敏。见 docs/ARCHITECTURE.md §4.16。服务端广播路径**必须且只能**经过这两个函数。
 *
 * ★ 红线 #2：**白名单逐字段构造**。禁止 `structuredClone` / `JSON.parse(JSON.stringify())`
 *   之后 `delete`，禁止展开 `HandState`。理由是默认行为：黑名单式脱敏在有人往
 *   `HandState` 加字段时**默认下发**，而漏网的那个字段通常正是新加的私密字段；
 *   白名单式脱敏的默认行为是「不下发」，新字段要泄漏必须有人手动把它抄进来。
 *   所以本文件里不允许出现任何 `...state` / `...player` / `...event`。
 *
 * ★ 红线 #1：`deck` / `deckIndex` / `burned` 一律不下发。`burned` 也不行——它是已知
 *   未出现的牌，会轻微改变胜率计算，且没有任何 UI 价值。
 *
 * ★ 红线 #3：`mucked === true` 时 `rank` 整体为 `null`（连 category 都不给，
 *   因为 `HandRank.bestFive` 含实际牌面）；`endedBy === 'fold'` 时 `showdown` 为空数组、
 *   `awards[*].handRank` 全为 `null`。这三条在引擎侧已经成立，这里**再独立执行一遍**：
 *   脱敏是最后一道闸门，不能把正确性押在上游。
 */
import type { AccountId, CardId, Chips, HandId, SeatIndex } from '../ids.js'
import type {
  AppliedAction,
  HandEvent,
  HandPhase,
  HandPlayer,
  HandRank,
  HandResult,
  HandState,
  PlayerHandStatus,
  Pot,
  PotAward,
  DeckVariant,
  ShowdownEntry,
  Street,
} from './types.js'
import { deckVariantOf } from './types.js'

// ============================================================================
// 对外视图形状
// ============================================================================

/** 可下发的手牌配置。刻意不含 `assist` 之外的策略解释，UI 只需要这些。 */
export interface PublicHandConfig {
  readonly handId: HandId
  readonly maxSeats: number
  readonly smallBlind: Chips
  readonly bigBlind: Chips
  readonly ante: Chips
  readonly anteMode: string
  readonly burnCards: boolean
  readonly showdownPolicy: string
  /** 玩法（公开信息）：客户端本地引擎、胜率、牌型提示都要按它算 */
  readonly deckVariant: DeckVariant
}

export interface PublicHandPlayer {
  readonly seat: SeatIndex
  readonly accountId: AccountId
  readonly startingStack: Chips
  readonly stack: Chips
  /** 只有本人的牌、或已对全场亮出的牌才非 null */
  readonly holeCards: readonly [CardId, CardId] | null
  /** 手里有没有牌（用来画牌背），与看不看得见无关 */
  readonly hasCards: boolean
  readonly status: PlayerHandStatus
  readonly committedThisRound: Chips
  readonly committedThisHand: Chips
  readonly hasActedThisRound: boolean
  readonly lastAction: AppliedAction['type'] | null
  readonly hasShownCards: boolean
}

export interface PublicShowdownRevealed {
  readonly seat: SeatIndex
  readonly revealOrder: number
  readonly mucked: false
  readonly holeCards: readonly [CardId, CardId]
  readonly rank: HandRank
}

export interface PublicShowdownMucked {
  readonly seat: SeatIndex
  readonly revealOrder: number
  readonly mucked: true
  readonly holeCards: null
  readonly rank: null
}

export type PublicShowdownEntry = PublicShowdownRevealed | PublicShowdownMucked

export interface PublicHandResult {
  readonly handId: HandId
  readonly handNo: number
  readonly endedBy: 'fold' | 'showdown'
  readonly buttonSeat: SeatIndex
  readonly smallBlindSeat: SeatIndex | null
  readonly bigBlindSeat: SeatIndex
  readonly startingStacks: Readonly<Record<SeatIndex, Chips>>
  readonly actionLog: readonly AppliedAction[]
  readonly board: readonly CardId[]
  readonly pots: readonly Pot[]
  readonly awards: readonly PotAward[]
  readonly uncalledReturn: { readonly seat: SeatIndex; readonly amount: Chips } | null
  readonly showdown: readonly PublicShowdownEntry[]
  readonly contributedBySeat: Readonly<Record<SeatIndex, Chips>>
  readonly wonBySeat: Readonly<Record<SeatIndex, Chips>>
  readonly netBySeat: Readonly<Record<SeatIndex, number>>
  readonly finalStacks: Readonly<Record<SeatIndex, Chips>>
}

/**
 * 引擎层的脱敏视图。
 *
 * 规格 §4.16 把返回类型写作 `PublicHand`，但 §6.5 的 `PublicHand` 含
 * `displayDeadlineAt` / `hardDeadlineAt` / `startedAt` 这些**服务端时钟**字段——
 * 引擎不读时钟（红线 #16），造不出来。所以引擎层定义自己的 `PublicHandState`，
 * 协议层的 `PublicHand` 由服务端在它之上补时间戳。
 */
export interface PublicHandState {
  readonly schemaVersion: number
  readonly handId: HandId
  readonly handNo: number
  readonly config: PublicHandConfig
  readonly viewerSeat: SeatIndex | null

  readonly buttonSeat: SeatIndex
  readonly smallBlindSeat: SeatIndex | null
  readonly bigBlindSeat: SeatIndex
  readonly isHeadsUp: boolean
  readonly deadButton: boolean
  readonly dealtSeats: readonly SeatIndex[]
  readonly players: Readonly<Record<SeatIndex, PublicHandPlayer>>

  /** 只含**已经发出**的公共牌。deck / deckIndex / burned 不在这里，也不在任何地方。 */
  readonly board: readonly CardId[]

  readonly phase: HandPhase
  readonly street: Street | null
  readonly betToMatch: Chips
  readonly lastFullRaiseSize: Chips
  readonly lastAggressorSeatThisStreet: SeatIndex | null
  readonly actionOn: SeatIndex | null

  readonly potTotal: Chips
  readonly pots: readonly Pot[]
  readonly actionLog: readonly AppliedAction[]
  readonly uncalledReturn: { readonly seat: SeatIndex; readonly amount: Chips } | null
  readonly showdown: readonly PublicShowdownEntry[]
  readonly result: PublicHandResult | null
}

// ============================================================================
// 逐字段构造
// ============================================================================

function publicConfig(state: HandState): PublicHandConfig {
  const c = state.config
  return {
    handId: c.handId,
    maxSeats: c.maxSeats,
    smallBlind: c.smallBlind,
    bigBlind: c.bigBlind,
    ante: c.ante,
    anteMode: c.anteMode,
    burnCards: c.burnCards,
    showdownPolicy: c.showdownPolicy,
    deckVariant: deckVariantOf(c),
  }
}

/** 唯一的底牌出口：本人的牌，或已经对全场亮出的牌。除此之外恒为 null。 */
function visibleHoleCards(
  p: HandPlayer,
  viewerSeat: SeatIndex | null,
): readonly [CardId, CardId] | null {
  if (viewerSeat !== null && p.seat === viewerSeat) return [p.holeCards[0], p.holeCards[1]]
  if (p.hasShownCards) return [p.holeCards[0], p.holeCards[1]]
  return null
}

function publicPlayer(p: HandPlayer, viewerSeat: SeatIndex | null): PublicHandPlayer {
  return {
    seat: p.seat,
    accountId: p.accountId,
    startingStack: p.startingStack,
    stack: p.stack,
    holeCards: visibleHoleCards(p, viewerSeat),
    // 弃牌即弃牌，牌背也收走
    hasCards: p.status !== 'folded',
    status: p.status,
    committedThisRound: p.committedThisRound,
    committedThisHand: p.committedThisHand,
    hasActedThisRound: p.hasActedThisRound,
    lastAction: p.lastAction,
    hasShownCards: p.hasShownCards,
  }
}

/** §4.16-3：盖牌者 `rank` 整体置 null。判别联合逼着这里先分支再构造。 */
function publicShowdownEntry(e: ShowdownEntry): PublicShowdownEntry {
  if (e.mucked) {
    return { seat: e.seat, revealOrder: e.revealOrder, mucked: true, holeCards: null, rank: null }
  }
  return {
    seat: e.seat,
    revealOrder: e.revealOrder,
    mucked: false,
    holeCards: [e.holeCards[0], e.holeCards[1]],
    rank: e.rank,
  }
}

function publicAward(a: PotAward, muckedSeats: ReadonlySet<SeatIndex>, foldEnded: boolean): PotAward {
  // §4.16-4/5：对应 entry 被 muck、或整手以弃牌收场时，handRank 一并置 null
  const handRank = foldEnded || muckedSeats.has(a.seat) ? null : a.handRank
  return {
    potIndex: a.potIndex,
    seat: a.seat,
    amount: a.amount,
    handRank,
    oddChip: a.oddChip,
  }
}

function publicPot(p: Pot): Pot {
  return {
    index: p.index,
    amount: p.amount,
    eligibleSeats: p.eligibleSeats.slice(),
    capLevel: p.capLevel,
  }
}

/** §4.16-7：`AppliedAction` 里不含任何牌面字段，逐字段抄写即可。 */
function publicAction(a: AppliedAction): AppliedAction {
  return {
    index: a.index,
    street: a.street,
    seat: a.seat,
    type: a.type,
    to: a.to,
    delta: a.delta,
    allIn: a.allIn,
    reopenedAction: a.reopenedAction,
    source: a.source,
    potBefore: a.potBefore,
    stackBefore: a.stackBefore,
    streetInvestedBefore: a.streetInvestedBefore,
    toCallBefore: a.toCallBefore,
    minRaiseToBefore: a.minRaiseToBefore,
    maxRaiseToBefore: a.maxRaiseToBefore,
    betsBefore: a.betsBefore,
    activeMask: a.activeMask,
    allInMask: a.allInMask,
  }
}

/**
 * 逐座位数值表的显式拷贝。写成循环而不是 `{ ...map }`：本文件的规矩是「白名单」，
 * 展开语法会把任何塞进这个表的非座位键一并带出去，而逐键校验只放行 `座位号 → 整数`。
 */
function copyBySeat(map: Readonly<Record<SeatIndex, number>>): Record<SeatIndex, number> {
  const out: Record<SeatIndex, number> = {}
  for (const [key, value] of Object.entries(map)) {
    const seat = Number(key)
    if (!Number.isInteger(seat) || typeof value !== 'number') continue
    out[seat] = value
  }
  return out
}

function redactResult(r: HandResult): PublicHandResult {
  // 红线 #3：fold 收场 ⟹ showdown 必须为空数组
  const foldEnded = r.endedBy === 'fold'
  const showdown = foldEnded ? [] : r.showdown.map(publicShowdownEntry)
  const muckedSeats = new Set<SeatIndex>()
  for (const e of r.showdown) if (e.mucked) muckedSeats.add(e.seat)

  return {
    handId: r.handId,
    handNo: r.handNo,
    endedBy: r.endedBy,
    buttonSeat: r.buttonSeat,
    smallBlindSeat: r.smallBlindSeat,
    bigBlindSeat: r.bigBlindSeat,
    startingStacks: copyBySeat(r.startingStacks),
    actionLog: r.actionLog.map(publicAction),
    board: r.board.slice(),
    pots: r.pots.map(publicPot),
    awards: r.awards.map((a) => publicAward(a, muckedSeats, foldEnded)),
    uncalledReturn:
      r.uncalledReturn === null
        ? null
        : { seat: r.uncalledReturn.seat, amount: r.uncalledReturn.amount },
    showdown,
    contributedBySeat: copyBySeat(r.contributedBySeat),
    wonBySeat: copyBySeat(r.wonBySeat),
    netBySeat: copyBySeat(r.netBySeat),
    finalStacks: copyBySeat(r.finalStacks),
  }
}

/**
 * `viewerSeat === null` 即旁观者视角：除已亮出的牌外看不到任何底牌。
 * 服务端必须从**连接身份**解析 viewerSeat（红线 #37），绝不采信客户端上报。
 */
export function redactHandState(
  state: HandState,
  viewerSeat: SeatIndex | null,
): PublicHandState {
  const players: Record<SeatIndex, PublicHandPlayer> = {}
  for (const p of Object.values(state.players)) {
    players[p.seat] = publicPlayer(p, viewerSeat)
  }

  const foldEnded = state.result !== null && state.result.endedBy === 'fold'
  const showdown = foldEnded ? [] : state.showdown.map(publicShowdownEntry)
  const handNo = (state as unknown as Record<string, unknown>)['handNo']

  return {
    schemaVersion: state.schemaVersion,
    handId: state.config.handId,
    handNo: typeof handNo === 'number' && Number.isSafeInteger(handNo) ? handNo : 0,
    config: publicConfig(state),
    viewerSeat,
    buttonSeat: state.buttonSeat,
    smallBlindSeat: state.smallBlindSeat,
    bigBlindSeat: state.bigBlindSeat,
    isHeadsUp: state.isHeadsUp,
    deadButton: state.deadButton,
    dealtSeats: state.dealtSeats.slice(),
    players,
    board: state.board.slice(),
    phase: state.phase,
    street: state.street,
    betToMatch: state.betToMatch,
    lastFullRaiseSize: state.lastFullRaiseSize,
    lastAggressorSeatThisStreet: state.lastAggressorSeatThisStreet,
    actionOn: state.actionOn,
    potTotal: state.potTotal,
    pots: state.pots.map(publicPot),
    actionLog: state.actionLog.map(publicAction),
    uncalledReturn:
      state.uncalledReturn === null
        ? null
        : { seat: state.uncalledReturn.seat, amount: state.uncalledReturn.amount },
    showdown,
    result: state.result === null ? null : redactResult(state.result),
  }
  // ↑ 这里没有 deck / deckIndex / burned，而且永远不该有。
}

// ============================================================================
// 事件脱敏
// ============================================================================

/**
 * §4.16-6。返回 `null` 表示该事件对这个 viewer 没有任何可下发内容，整条丢弃。
 *
 * `cardsRevealed` 直接透传——它是 `revealSeat()` 的产物，是别人底牌**唯一**的外泄闸门，
 * 能走到这里就说明引擎已经判定这张牌该对全场公开。
 */
export function redactEvent(event: HandEvent, viewerSeat: SeatIndex | null): HandEvent | null {
  switch (event.t) {
    case 'holeCardsDealt': {
      // 只保留本人的两张牌。旁观者没有任何可看的内容 → 整条丢弃，由快照的
      // players[*].hasCards 驱动发牌动画。
      if (viewerSeat === null) return null
      const mine = event.cardsBySeat[viewerSeat]
      if (mine === undefined) return null
      return {
        t: 'holeCardsDealt',
        cardsBySeat: { [viewerSeat]: [mine[0], mine[1]] as readonly [CardId, CardId] },
      }
    }

    case 'handComplete':
      return { t: 'handComplete', result: redactResult(event.result) }

    case 'handStarted':
    case 'blindPosted':
    case 'actionOn':
    case 'playerActed':
    case 'streetDealt':
    case 'uncalledReturned':
    case 'potsUpdated':
    case 'runItOut':
    case 'cardsRevealed':
    case 'cardsMucked':
    case 'potAwarded':
      // 全部只含公开信息（`potAwarded.award.handRank` 只会在摊牌路径上非空，
      // 而摊牌路径上该座位一定已经亮过牌）
      return event
  }
}
